import React, { useState, useEffect, useCallback } from 'react';
import { setGoogleSignInCallback, renderGoogleSignInButton } from '../utils/googleSignIn';

const API_BASE = process.env.REACT_APP_API_URL || '';

function Register({ onRegisterSuccess, onBackToLogin, onLogin, googleData }) {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    phone: '',
    employeeId: '',
    designation: '',
    department: '',
    location: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isGoogleSSO, setIsGoogleSSO] = useState(false);
  const [googleCredential, setGoogleCredential] = useState(null);

  // Handle Google SSO data passed from Login
  useEffect(() => {
    if (googleData) {
      setIsGoogleSSO(true);
      setGoogleCredential(googleData.credential);
      setFormData(prev => ({
        ...prev,
        email: googleData.email || '',
        name: googleData.name || ''
      }));
    }
  }, [googleData]);

  const handleGoogleResponse = useCallback(async (response) => {
    setError('');
    setLoading(true);

    try {
      const res = await fetch(`${API_BASE}/api/auth/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ credential: response.credential })
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Google sign-in failed');
      }

      if (data.needsRegistration) {
        // New user - fill in email and name from Google
        setIsGoogleSSO(true);
        setGoogleCredential(response.credential);
        setFormData(prev => ({
          ...prev,
          email: data.email || '',
          name: data.name || ''
        }));
      } else {
        // Existing user - logged in
        onLogin(data.user, data.token);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [onLogin]);

  useEffect(() => {
    if (isGoogleSSO) return;

    setGoogleSignInCallback(handleGoogleResponse);
    const container = document.getElementById('google-signup-btn');
    let cancelled = false;

    renderGoogleSignInButton(container, { text: 'signup_with' }).catch(() => {});

    return () => {
      cancelled = true;
      if (cancelled && container) container.innerHTML = '';
    };
  }, [handleGoogleResponse, isGoogleSSO]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validate email domain
    if (!formData.email.endsWith('@printo.in') && !formData.email.endsWith('@canvera.com')) {
      setError('Email must be a @printo.in or @canvera.com address');
      return;
    }

    // Validate phone (10–15 digits, optional +) and employee ID (digits only)
    if (!/^\+?\d{10,15}$/.test((formData.phone || '').replace(/[\s\-()]/g, ''))) {
      setError('Enter a valid phone number (10–15 digits)');
      return;
    }
    if (!/^\d+$/.test((formData.employeeId || '').trim())) {
      setError('Employee ID must be digits only');
      return;
    }

    if (isGoogleSSO) {
      // Google SSO registration - complete profile
      setLoading(true);

      try {
        const response = await fetch(`${API_BASE}/api/auth/google/register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            credential: googleCredential,
            phone: formData.phone,
            employeeId: formData.employeeId,
            designation: formData.designation,
            department: formData.department,
            location: formData.location
          })
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'Registration failed');
        }

        if (data.token) {
          // Auto-logged in (first user or auto-approved)
          onLogin(data.user, data.token);
        } else {
          setSuccess(data.message);
          // Redirect to login after 2 seconds
          setTimeout(() => {
            onRegisterSuccess();
          }, 2000);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    } else {
      // Regular email/password registration
      // Validate password
      if (formData.password.length < 6) {
        setError('Password must be at least 6 characters');
        return;
      }

      // Confirm password
      if (formData.password !== formData.confirmPassword) {
        setError('Passwords do not match');
        return;
      }

      setLoading(true);

      try {
        const response = await fetch(`${API_BASE}/api/auth/register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: formData.email,
            password: formData.password,
            name: formData.name,
            phone: formData.phone,
            employeeId: formData.employeeId,
            designation: formData.designation,
            department: formData.department,
            location: formData.location
          })
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'Registration failed');
        }

        if (data.token) {
          onLogin(data.user, data.token);
        } else {
          setSuccess(data.message);
          setTimeout(() => onRegisterSuccess(), 2000);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="login-container">
      <div className="login-card register-card">
        <h1>{isGoogleSSO ? 'Complete Your Profile' : 'Create Account'}</h1>
        <p>{isGoogleSSO ? 'Please fill in your employee details' : 'Register with your company email'}</p>

        {error && (
          <div className="error-message">{error}</div>
        )}

        {success && (
          <div className="success-message">{success}</div>
        )}

        {/* Google Sign-Up Button - only show if not in SSO mode */}
        {!isGoogleSSO && (
          <>
            <div className="google-signin-wrapper">
              <div id="google-signup-btn"></div>
            </div>

            <div className="divider">
              <span>or register with email</span>
            </div>
          </>
        )}

        <form onSubmit={handleSubmit}>
          {!isGoogleSSO && (
            <div className="form-section">
              <h3>Account Details</h3>
              <input
                type="email"
                name="email"
                placeholder="Email (@printo.in / @canvera.com) *"
                value={formData.email}
                onChange={handleChange}
                required
              />
              <div className="password-field">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  placeholder="Password (min 6 characters) *"
                  value={formData.password}
                  onChange={handleChange}
                  required
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(v => !v)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                      <path d="M1 1l22 22" />
                      <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>
              <div className="password-field">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  placeholder="Confirm Password *"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(v => !v)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                      <path d="M1 1l22 22" />
                      <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
          )}

          {isGoogleSSO && (
            <div className="sso-info">
              <div className="sso-email">
                <span className="sso-label">Google Account:</span>
                <span className="sso-value">{formData.email}</span>
              </div>
              <div className="sso-name">
                <span className="sso-label">Name:</span>
                <span className="sso-value">{formData.name}</span>
              </div>
            </div>
          )}

          <div className="form-section">
            <h3>Profile Information</h3>
            {!isGoogleSSO && (
              <input
                type="text"
                name="name"
                placeholder="Full Name *"
                value={formData.name}
                onChange={handleChange}
                required
              />
            )}
            <input
              type="tel"
              name="phone"
              inputMode="tel"
              placeholder="Phone Number * (10–15 digits)"
              value={formData.phone}
              onChange={handleChange}
              required
            />
            <input
              type="text"
              name="employeeId"
              inputMode="numeric"
              placeholder="Employee ID * (numbers only)"
              value={formData.employeeId}
              onChange={handleChange}
              required
            />
            <input
              type="text"
              name="designation"
              placeholder="Designation *"
              value={formData.designation}
              onChange={handleChange}
              required
            />
            <input
              type="text"
              name="department"
              placeholder="Department *"
              value={formData.department}
              onChange={handleChange}
              required
            />
            <input
              type="text"
              name="location"
              placeholder="Location *"
              value={formData.location}
              onChange={handleChange}
              required
            />
          </div>

          <button type="submit" disabled={loading}>
            {loading ? 'Registering...' : (isGoogleSSO ? 'Complete Registration' : 'Register')}
          </button>
        </form>

        <div className="login-footer">
          <p>Already have an account?</p>
          <button
            type="button"
            className="link-btn"
            onClick={onBackToLogin}
          >
            Sign in here
          </button>
        </div>
      </div>
    </div>
  );
}

export default Register;
